/**
 * Read shape of `GET /ar/invoices/:invoiceId/revenue-tax-provisions` (ADR 0024, owner decision H2,
 * ALC-4411): the PIS/COFINS provision of an AR invoice, one item per book × revenue account × tax.
 * Zero rows (EXPORT, NOT_TAXED…) and PENDING rows are listed with their reason so the screen can
 * say why nothing (or not yet anything) was provisioned. Money and rates are exact decimal strings.
 */
import type {
  RevenueTaxLegacyTransitionTreatment,
  RevenueTaxSourceEventType,
} from './generated/enums.js';
import type { RevenueTaxReasonCode } from './revenue-tax.js';

export interface RevenueTaxLegacyTransitionClassificationRequest {
  treatment: RevenueTaxLegacyTransitionTreatment;
  evidenceCode: string;
  evidenceDescription: string;
}

export interface RevenueTaxLegacyTransitionClassificationResponse {
  sourceEventType: RevenueTaxSourceEventType;
  sourceEventId: string;
  treatment: RevenueTaxLegacyTransitionTreatment;
  evidenceCode: string;
  evidenceDescription: string;
  classifiedByUserId: string;
  classifiedAt: string;
  provisionIds: string[];
}

export type ArInvoiceRevenueTaxType = 'PIS' | 'COFINS';

export const REVENUE_TAX_EXPORT_ELIGIBILITY_ROUTES = [
  'DIRECT_GOODS',
  'SERVICE_FX_INGRESS',
  'SERVICE_FUNDS_RETAINED_ABROAD',
  'COMMERCIAL_EXPORTER',
] as const;

export type RevenueTaxExportEligibilityRequestRoute =
  (typeof REVENUE_TAX_EXPORT_ELIGIBILITY_ROUTES)[number];

export const REVENUE_TAX_EXPORT_DOCUMENT_ROLES = [
  'CUSTOMS_EXPORT_OPERATION',
  'SERVICE_FX_FULL_OPERATION',
  'SERVICE_FUNDS_ABROAD_FULL_OPERATION',
] as const;
export type RevenueTaxExportDocumentRole = (typeof REVENUE_TAX_EXPORT_DOCUMENT_ROLES)[number];

export interface RevenueTaxExportEligibilityRequest {
  sourceJournalId: string;
  revenueAccountId: string;
  route: RevenueTaxExportEligibilityRequestRoute;
  evidenceDocumentId: string;
  receiptId?: string;
  qualifyingPaymentDate?: string;
  qualifyingPaymentCurrency?: string;
  qualifyingPaymentAmount?: string;
  evidenceCode: string;
  evidenceDescription: string;
}

export interface RevenueTaxExportEligibilityPreview {
  invoiceId: string;
  sourcePostingId: string;
  entityId: string;
  sourceJournalId: string;
  sourceJournalLineIds: string[];
  revenueAccountId: string;
  sourceBookId: string;
  sourceVersionId: string;
  sourceFunctionalCurrency: string;
  sourceNetAmount: string;
  invoiceCurrency: string;
  invoiceNetAmount: string;
  route: RevenueTaxExportEligibilityRequestRoute;
  customerPartyId: string;
  customerCountryCode: string;
  lineKinds: ('GOODS' | 'SERVICE' | 'UNKNOWN')[];
  evidenceDocumentId: string;
  evidenceDocumentHashSha256: string;
  evidenceDocumentRole: RevenueTaxExportDocumentRole;
  receiptId: string | null;
  receiptAllocationId: string | null;
  receiptJournalId: string | null;
  receiptPeriodId: string | null;
  bankTransactionId: string | null;
  reconciliationLinkId: string | null;
  reconciliationGroupId: string | null;
  reconciliationGroupStatus: string | null;
  reconciliationPostingStatus: string | null;
  reconciliationJournalId: string | null;
  receiptStatus: string | null;
  allocationStatus: string | null;
  reversalAuditId: string | null;
  reversalJournalIds: string[];
  qualifyingAllocationIds: string[];
  qualifyingPaymentDate: string | null;
  qualifyingPaymentCurrency: string | null;
  qualifyingPaymentAmount: string | null;
  evidenceCode: string;
  evidenceDescription: string;
  legalBasis: string;
  calculationFingerprint: string;
  existingDecisionId: string | null;
}

export interface RevenueTaxExportEligibilityConfirmRequest extends RevenueTaxExportEligibilityRequest {
  expectedFingerprint: string;
}

export interface RevenueTaxExportEligibilityDecisionResponse extends RevenueTaxExportEligibilityPreview {
  decisionId: string;
  previousDecisionId: string | null;
  reviewedByUserId: string;
  reviewedAt: string;
  healedProvisionIds: string[];
}

export interface RevenueTaxExportEvidenceDocument {
  documentId: string;
  originalName: string;
  mimeType: string;
  fileHashSha256: string;
  createdAt: string;
  usedByDecisionIds: string[];
  evidenceRole: RevenueTaxExportDocumentRole;
}

export interface RevenueTaxExportReceiptOption {
  receiptId: string;
  receiptNumber: string | null;
  receiptDate: string | null;
  currencyCode: string | null;
  receiptAmount: string | null;
  allocatedAmount: string;
}

export interface RevenueTaxExportEligibilitySetup {
  documents: RevenueTaxExportEvidenceDocument[];
  receipts: RevenueTaxExportReceiptOption[];
}

export interface ArInvoiceRevenueTaxProvisionItem {
  provisionId: string;
  bookId: string;
  bookCode: string | null;
  bookName: string;
  /** The tax: `PIS` | `COFINS` (from the liability slot `PIS_FATURAMENTO` / `COFINS_FATURAMENTO`). */
  taxType: ArInvoiceRevenueTaxType;
  taxSlotCode: string;
  /** The invoice revenue journal this provision belongs to. */
  revenueJournalId: string;
  revenueAccountId: string;
  revenueAccountCode: string | null;
  revenueAccountName: string | null;
  /** Revenue Class snapshot at posting; null on a MISSING_REVENUE_CLASS row. */
  revenueClass: string | null;
  /** Net revenue base (functional currency, 2 decimals; may be negative). */
  baseAmount: string;
  /** Rate applied (e.g. "0.0065"); null when no rule resolved. */
  rate: string | null;
  /** Tax provisioned (2 decimals); "0.00" when nothing was posted. */
  amount: string;
  /** `POSTED` | `NOT_APPLICABLE` | `PENDING` | `REVERSED` | … (RevenueTaxProvisionStatus). */
  status: string;
  /** Stable reason code (`RevenueTaxReasonCode`), or null on a plain POSTED row. */
  reason: RevenueTaxReasonCode | string | null;
  provisionJournalId: string | null;
  /** Tax already reversed by cancels (2 decimals). */
  reversedAmount: string;
  /** Revenue date (YYYY-MM-DD) and assessment period (YYYY-MM) of the provision. */
  revenueDate: string;
  assessmentPeriod: string;
  functionalCurrency: string;
  /** Frozen source basis used to determine whether pre-2027 cash transition review applies. */
  dueBasis: 'ACCRUAL' | 'CASH';
  legacyTransitionTreatment: RevenueTaxLegacyTransitionTreatment | null;
  legacyTransitionEvidenceCode: string | null;
  legacyTransitionEvidenceDescription: string | null;
  legacyTransitionClassifiedByUserId: string | null;
  legacyTransitionClassifiedAt: string | null;
  exportEligibility?: null | {
    decisionId: string;
    previousDecisionId: string | null;
    evidenceState: 'CURRENT' | 'INVALID_PROOF' | 'HISTORICAL_SOURCE_REVERSED';
    invalidReason: string | null;
    route: RevenueTaxExportEligibilityRequestRoute;
    sourceJournalLineIds: string[];
    sourceBookId: string;
    sourceVersionId: string;
    sourceFunctionalCurrency: string;
    sourceNetAmount: string;
    invoiceCurrency: string;
    invoiceNetAmount: string;
    customerPartyId: string;
    customerCountryCode: string;
    evidenceDocumentId: string;
    evidenceDocumentHashSha256: string;
    evidenceDocumentRole: RevenueTaxExportDocumentRole;
    receiptId: string | null;
    qualifyingAllocationIds: string[];
    qualifyingPaymentDate: string | null;
    qualifyingPaymentCurrency: string | null;
    qualifyingPaymentAmount: string | null;
    evidenceCode: string;
    evidenceDescription: string;
    legalBasis: string;
    calculationFingerprint: string;
    reviewedByUserId: string;
    reviewedAt: string;
  };
}
